import React, { useMemo } from 'react'
import styled from 'styled-components/macro'
import {
  PiMegaphoneFill,
  PiMegaphone,
  PiHandCoinsFill,
  PiHandCoins,
  PiSealPercentFill,
  PiSealPercent,
  PiHandshakeFill,
  PiHandshake,
  PiChartLineUpFill,
  PiChartLineUp,
  PiGearFill,
  PiGear,
  PiQuestionFill,
  PiQuestion,
  PiSignOutFill,
  PiSignOut,
  PiListFill,
  PiList,
  PiBellFill,
  PiBell,
  PiTrayFill,
  PiTray,
} from 'react-icons/pi'
import { TargetArrowIcon, TargetArrowOutlineIcon } from './nav-icons'
import type { NavIconType } from './types'

interface LeftNavIconButtonProps {
  icon: NavIconType
  label: string
  isSelected?: boolean
  onClick?: () => void
  className?: string
}

const ICON_SIZE = 24

const IconWrapper = styled.button<{ $isSelected: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border-radius: 8px;
  border: none;
  background: ${({ $isSelected }) =>
    $isSelected ? 'var(--nav-bg-hover)' : 'transparent'};
  color: var(--nav-text-primary);
  cursor: pointer;
  transition: background 0.15s ease-in-out;
  position: relative;

  &:hover {
    background: var(--nav-bg-hover);
  }

  &:focus {
    outline: 2px solid var(--nav-text-muted);
    outline-offset: 2px;
  }

  &:active {
    background: var(--nav-bg-hover);
  }
`

const Tooltip = styled.div`
  position: absolute;
  left: calc(100% + 12px);
  top: 50%;
  transform: translateY(-50%);
  background: var(--nav-bg-primary);
  color: var(--nav-text-primary);
  padding: 6px 12px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 500;
  white-space: nowrap;
  opacity: 0;
  visibility: hidden;
  transition:
    opacity 0.15s ease-in-out,
    visibility 0.15s ease-in-out;
  pointer-events: none;
  z-index: 100;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);

  ${IconWrapper}:hover & {
    opacity: 1;
    visibility: visible;
  }

  /* Arrow pointing left */
  &::before {
    content: '';
    position: absolute;
    left: -6px;
    top: 50%;
    transform: translateY(-50%);
    border: 6px solid transparent;
    border-right-color: var(--nav-bg-primary);
    border-left: none;
  }
`

export const LeftNavIconButton: React.FC<LeftNavIconButtonProps> = ({
  icon,
  label,
  isSelected = false,
  onClick,
  className,
}) => {
  const IconComponent = useMemo(() => {
    const iconMap: Record<
      NavIconType,
      { filled: React.ReactNode; outline: React.ReactNode }
    > = {
      target: {
        filled: <TargetArrowIcon size={ICON_SIZE} />,
        outline: <TargetArrowOutlineIcon size={ICON_SIZE} />,
      },
      megaphone: {
        filled: <PiMegaphoneFill size={ICON_SIZE} />,
        outline: <PiMegaphone size={ICON_SIZE} />,
      },
      'hand-coins': {
        filled: <PiHandCoinsFill size={ICON_SIZE} />,
        outline: <PiHandCoins size={ICON_SIZE} />,
      },
      'seal-percent': {
        filled: <PiSealPercentFill size={ICON_SIZE} />,
        outline: <PiSealPercent size={ICON_SIZE} />,
      },
      handshake: {
        filled: <PiHandshakeFill size={ICON_SIZE} />,
        outline: <PiHandshake size={ICON_SIZE} />,
      },
      'chart-line-up': {
        filled: <PiChartLineUpFill size={ICON_SIZE} />,
        outline: <PiChartLineUp size={ICON_SIZE} />,
      },
      workspace: {
        filled: <PiTrayFill size={ICON_SIZE} />,
        outline: <PiTray size={ICON_SIZE} />,
      },
      gear: {
        filled: <PiGearFill size={ICON_SIZE} />,
        outline: <PiGear size={ICON_SIZE} />,
      },
      question: {
        filled: <PiQuestionFill size={ICON_SIZE} />,
        outline: <PiQuestion size={ICON_SIZE} />,
      },
      'sign-out': {
        filled: <PiSignOutFill size={ICON_SIZE} />,
        outline: <PiSignOut size={ICON_SIZE} />,
      },
      list: {
        filled: <PiListFill size={ICON_SIZE} />,
        outline: <PiList size={ICON_SIZE} />,
      },
      bell: {
        filled: <PiBellFill size={ICON_SIZE} />,
        outline: <PiBell size={ICON_SIZE} />,
      },
    }

    return isSelected ? iconMap[icon].filled : iconMap[icon].outline
  }, [icon, isSelected])

  return (
    <IconWrapper
      $isSelected={isSelected}
      onClick={onClick}
      className={className}
      aria-label={label}
      title=""
    >
      {IconComponent}
      <Tooltip>{label}</Tooltip>
    </IconWrapper>
  )
}
